import {
  useState,
  useEffect,
  SetStateAction,
  useCallback
} from "react";
import { toast } from "react-toastify";
import * as H from "history";

import {
  CreateEditUserGroup,
  UpdateUserGroupParams,
  useUserApi
} from "../api/users.api";

export interface UseUserGroupReturn {
  isLoading: UserGroupLoadingState;
  userGroup: CreateEditUserGroup | null;
  setCurrentUserGroup: React.Dispatch<SetStateAction<CreateEditUserGroup | null>>
  createUserGroupReq: (userGroup: CreateEditUserGroup) => void;
  updateUserGroupReq: (data: UpdateUserGroupParams) => void;
  getUserGroupReq: (data: { id: number }) => void;
}

const initialStateForCreate = {
  name: "",
  managerId: ""
};

export interface UserGroupLoadingState {
  create: boolean;
  update: boolean;
  get: boolean;
}

/**
 * Custom hook used to manage local user group state as well as all API interactions
 */
const useUserGroup = (history: H.History, userGroupId?: number): UseUserGroupReturn => {
  const [ getUserGroupRes, getUserGroupReq ] = useUserApi("GET_USER_GROUP");
  const [ createUserGroupRes, createUserGroupReq ] = useUserApi("CREATE_USER_GROUP");
  const [ updateUserGroupRes, updateUserGroupReq ] = useUserApi("UPDATE_USER_GROUP");

  const [ isLoading, setIsLoading ] = useState<UserGroupLoadingState>(() => ({
    create: false,
    update: false,
    get: false
  }));

  // if user group id is provided, wait for get request to complete which will set currentUserGroup value
  const [ currentUserGroup, setCurrentUserGroup ] = useState<CreateEditUserGroup | null>(
    () => !userGroupId ? initialStateForCreate : null
  );

  // helper for unknown error string
  const unknownErrorString = useCallback((type: "create" | "update" | "get") => {
    return `Unable to ${type} user group`;
  }, []);

  // fetch user group if id provided
  useEffect(() => {
    if (userGroupId) {
      getUserGroupReq({ id: userGroupId });
    }
  }, [ userGroupId, getUserGroupReq ]);

  useEffect(() => {
    // handle create API response
    if (createUserGroupRes.data) {
      setCurrentUserGroup(createUserGroupRes.data);
      toast.success("Successfully created user group.");
      history.push(`/admin/users/groups/edit/${createUserGroupRes.data.id}`);
    }

    if (createUserGroupRes.error) {
      toast.error(createUserGroupRes.error.message || unknownErrorString("create"));
    }
  }, [
    createUserGroupRes,
    history,
    unknownErrorString
  ]);

  // update local state on update API response
  useEffect(() => {
    if (updateUserGroupRes.data) {
      setCurrentUserGroup(updateUserGroupRes.data);
      toast.success("Successfully updated user group.");
    }

    if (updateUserGroupRes.error) {
      toast.error(updateUserGroupRes.error.message || unknownErrorString("update"));
    }
  }, [ unknownErrorString, updateUserGroupRes ]);

  // update local state when getting userGroup
  useEffect(() => {
    if (getUserGroupRes.data) {
      setCurrentUserGroup(getUserGroupRes.data);
    }

    if (getUserGroupRes.error) {
      toast.error(getUserGroupRes.error.message || unknownErrorString("get"));
    }
  }, [ getUserGroupRes, unknownErrorString ]);

  // set loading state if any of the requests status updated
  useEffect(() => {
    setIsLoading({
      create: createUserGroupRes.pending,
      update: updateUserGroupRes.pending,
      get: getUserGroupRes.pending
    });
  }, [
    updateUserGroupRes,
    createUserGroupRes,
    getUserGroupRes
  ]);

  return {
    isLoading,
    userGroup: currentUserGroup,
    setCurrentUserGroup,
    createUserGroupReq,
    updateUserGroupReq,
    getUserGroupReq
  };
};

export default useUserGroup;